import { CreatableViewModel, PersistenceModel, StoreModel, ViewModel } from 'lib/domain/model/Model';
import { ModelConverter } from 'lib/domain/model/ModelConverter';

import { HttpConnector } from './HttpConnector';

export abstract class HttpPersistence<PModel extends PersistenceModel,
	SModel extends StoreModel,
	VModel extends ViewModel,
	CVModel extends CreatableViewModel> {

	protected abstract serviceApiControllerUri: string;
	protected abstract collectionControllerUri: string;
	protected abstract entityControllerUri: string;

	protected abstract responseCollectionKey: string;
	protected abstract responseEntityKey: string;

	protected abstract modelConverter: ModelConverter<PModel, SModel, VModel, CVModel>;

	private readonly connector: HttpConnector;
	private readonly clientUuid: string;
	private readonly facilityUuid: string;

	public constructor(clientUuid: string, facilityUuid: string) {
		this.connector = new HttpConnector();
		this.clientUuid = clientUuid;
		this.facilityUuid = facilityUuid;
	}

	public async fetchCollection(endpointBuilder?: () => string): Promise<Array<SModel>> {
		let endpoint: string;
		if (endpointBuilder !== undefined) {
			endpoint = endpointBuilder();
		} else {
			endpoint = process.env.REACT_APP_API_BASE_URL + this.serviceApiControllerUri + '/'
				+ this.clientUuid + '/' + this.facilityUuid + '/' + this.collectionControllerUri;
		}
		const parsedBody = await this.connector.get(endpoint);
		const persistenceModelCollection = parsedBody[this.responseCollectionKey] as Array<PModel>;
		return persistenceModelCollection.map((persistenceModel): SModel => {
			return this.modelConverter.fromPersistenceModel(persistenceModel).toStoreModel();
		});
	}

	public async fetchLatestCollection(endpointBuilder?: () => string): Promise<Array<SModel>> {
		let endpoint: string;
		if (endpointBuilder !== undefined) {
			endpoint = endpointBuilder();
		} else {
			endpoint = process.env.REACT_APP_API_BASE_URL + this.serviceApiControllerUri + '/'
				+ this.clientUuid + '/' + this.facilityUuid + '/' + this.collectionControllerUri + '/latest';
		}
		const parsedBody = await this.connector.get(endpoint);
		const persistenceModelCollection = parsedBody[this.responseCollectionKey] as Array<PModel>;
		return persistenceModelCollection.map((persistenceModel): SModel => {
			return this.modelConverter.fromPersistenceModel(persistenceModel).toStoreModel();
		});
	}

	public async fetch(uuid: string): Promise<SModel | null> {
		const endpoint = process.env.REACT_APP_API_BASE_URL + this.serviceApiControllerUri + '/'
			+ this.clientUuid + '/' + this.facilityUuid + '/' + this.entityControllerUri + '/' + uuid;
		const parsedBody = await this.connector.get(endpoint);
		const persistenceModel = parsedBody[this.responseEntityKey] as PModel;
		return this.modelConverter.fromPersistenceModel(persistenceModel).toStoreModel();
	}

	public async update(storeModel: SModel): Promise<SModel | null> {
		const endpoint = process.env.REACT_APP_API_BASE_URL + this.serviceApiControllerUri + '/'
			+ this.clientUuid + '/' + this.facilityUuid + '/' + this.entityControllerUri + '/' + storeModel.Uuid;
		let persistenceModel = this.modelConverter.fromStoreModel(storeModel).toPersistenceModel();
		const parsedBody = await this.connector.put(endpoint, persistenceModel);
		persistenceModel = parsedBody[this.responseEntityKey] as PModel;
		return this.modelConverter.fromPersistenceModel(persistenceModel).toStoreModel();
	}

	public async create(storeModel: SModel): Promise<SModel> {
		const endpoint = process.env.REACT_APP_API_BASE_URL + this.serviceApiControllerUri + '/'
			+ this.clientUuid + '/' + this.facilityUuid + '/' + this.entityControllerUri;
		let persistenceModel = this.modelConverter.fromStoreModel(storeModel).toPersistenceModel();
		const parsedBody = await this.connector.post(endpoint, persistenceModel);
		persistenceModel = parsedBody[this.responseEntityKey] as PModel;
		return this.modelConverter.fromPersistenceModel(persistenceModel).toStoreModel();
	}

	public async delete(storeModel: SModel): Promise<SModel | null> {
		const endpoint = process.env.REACT_APP_API_BASE_URL + this.serviceApiControllerUri + '/'
			+ this.clientUuid + '/' + this.facilityUuid + '/' + this.entityControllerUri + '/' + storeModel.Uuid;
		const parsedBody = await this.connector.delete(endpoint);
		const persistenceModel = parsedBody[this.responseEntityKey] as PModel;
		return this.modelConverter.fromPersistenceModel(persistenceModel).toStoreModel();
	}

	getClientUuid(): string {
		return this.clientUuid;
	}

	getFacilityUuid(): string {
		return this.facilityUuid;
	}
}
